import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from './firebase';

export const signUp = async (
  email: string, 
  password: string, 
  displayName?: string
) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  
  if (displayName && userCredential.user) {
    await updateProfile(userCredential.user, { displayName });
  }
  
  // Send Firebase's built-in email verification with custom redirect
  try {
    const actionCodeSettings = {
      // After email verification, redirect to /app which will show setup modal
      url: `${window.location.origin}/app?setup=true`,
      handleCodeInApp: true,
    };
    await sendEmailVerification(userCredential.user, actionCodeSettings);
  } catch (error) {
    console.error('Error sending verification email:', error);
    // Don't throw here - user is created but email failed
  }
  
  return userCredential;
};

export const signIn = async (email: string, password: string) => {
  console.log('📡 Firebase Auth: Starting sign in request...');
  console.log('📡 Auth endpoint: https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword');

  // Test basic network connectivity first
  console.log('🧪 Testing basic network request to Google...');
  try {
    await fetch('https://www.google.com', {
      method: 'HEAD',
      mode: 'no-cors'
    });
    console.log('✅ Basic network test successful');
  } catch (testError) {
    console.error('❌ Basic network test FAILED:', testError);
  }

  console.log('📡 Now attempting Firebase sign in...');
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    console.log('✅ Firebase sign in successful');
    return result;
  } catch (error) {
    console.error('❌ Firebase sign in error:', error);
    throw error;
  }
};

export const logOut = async () => {
  // Don't clear biometric credentials - keep them for next login
  return await signOut(auth);
};

export const resetPassword = async (email: string) => {
  const actionCodeSettings = {
    url: `${window.location.origin}/reset-password`,
    handleCodeInApp: false,
  };
  return await sendPasswordResetEmail(auth, email, actionCodeSettings);
};

export const verifyEmail = async (user: User) => {
  const actionCodeSettings = {
    // After email verification, redirect to /app which will show setup modal
    url: `${window.location.origin}/app?setup=true`,
    handleCodeInApp: true,
  };
  return await sendEmailVerification(user, actionCodeSettings);
};

export const resendVerificationEmail = async (user: User) => {
  const actionCodeSettings = {
    // After email verification, redirect to /app which will show setup modal
    url: `${window.location.origin}/app?setup=true`,
    handleCodeInApp: true,
  };
  return await sendEmailVerification(user, actionCodeSettings);
};

export const checkEmailVerified = (user: User | null): boolean => {
  return user?.emailVerified || false;
};

export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

export { auth };